import notify from '@/libraries/notify';
import axios from 'axios';

// Inline timezone detection to avoid import issues
const detectTimezone = () => {
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone && timezone !== 'UTC') {
      return timezone;
    }
  }
  return 'UTC';
};

// Track ongoing refresh requests to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshSubscribers = [];

// Export these so tokenRefreshService can use them
export const getIsRefreshing = () => isRefreshing;
export const setIsRefreshing = (value) => {
  isRefreshing = value;
};

// Helper to add request to queue
const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

// Helper to process queued requests
const processRefreshSubscribers = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Helper to clear queued requests with error
const clearRefreshSubscribers = () => {
  refreshSubscribers.forEach((callback) => callback(null));
  refreshSubscribers = [];
};

// Setup axios interceptors
export const setupAxiosInterceptors = (getAccessToken, setAccessToken, clearAuthStorage) => {
  // Request interceptor - add auth header to requests
  axios.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add timezone header for all requests
      const timezone = detectTimezone();
      if (timezone && !config.headers['X-Timezone']) {
        config.headers['X-Timezone'] = timezone;
      }

      // Add PostHog session replay headers for linking logs to session replay
      if (window.posthog) {
        try {
          const distinctId = window.posthog.getDistinctId?.();
          const sessionId = window.posthog.getSessionId?.();
          if (distinctId && !config.headers['x-posthog-distinct-id']) {
            config.headers['x-posthog-distinct-id'] = distinctId;
          }
          if (sessionId && !config.headers['x-posthog-session-id']) {
            config.headers['x-posthog-session-id'] = sessionId;
          }
        } catch (error) {
          // Silently ignore PostHog errors to prevent request failures
          console.debug('PostHog session ID retrieval failed:', error);
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Response interceptor - handle token refresh
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Skip token refresh for auth endpoints to prevent infinite loops
      const isAuthEndpoint =
        originalRequest.url?.includes('/auth/') ||
        originalRequest.url?.includes('/login') ||
        originalRequest.url?.includes('/get-started');

      // If we get a 401, haven't already tried to refresh, and it's not an auth endpoint
      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true;

        // If we're already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            addRefreshSubscriber((token) => {
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(axios(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }

        isRefreshing = true;

        try {
          // Call refresh endpoint - ensure cookies are sent
          const refreshResponse = await axios.post(
            '/auth/refresh',
            {},
            {
              withCredentials: true,
            },
          );

          if (refreshResponse.data?.data?.accessToken) {
            const newAccessToken = refreshResponse.data.data.accessToken;

            // Update the stored access token
            setAccessToken(newAccessToken);

            // Update the original request with new token
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

            // Process any queued requests
            processRefreshSubscribers(newAccessToken);

            // Retry the original request
            return axios(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, clear auth data and redirect to login

          // Clear any queued requests
          clearRefreshSubscribers();

          // Clear all stored auth data (user, businesses, selectedBusiness, etc.)
          if (clearAuthStorage) {
            clearAuthStorage();
          } else {
            // Fallback if clearAuthStorage not provided
            setAccessToken(null);
          }

          // Show notification to user
          notify.error({
            title: 'Session Expired',
            message: 'Your session has expired. Please log in again.',
          });

          // AuthInitializer will handle the redirect via proactive refresh failure
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Handle account under review (403 with ACCOUNT_UNDER_REVIEW error code)
      if (error.response?.status === 403 && error.response?.data?.errorCode === 'ACCOUNT_UNDER_REVIEW') {
        if (clearAuthStorage) {
          clearAuthStorage();
        } else {
          setAccessToken(null);
        }
        window.location.href = '/under-review';
        return Promise.reject(error);
      }

      return Promise.reject(error);
     },
   );
 };

export default setupAxiosInterceptors;
